import { useCallback, useEffect, useRef, useState } from "react";
import type { AssetRef, BuilderApi } from "../api/contract";
import { prepareReference } from "../lib/photos";

/** Inspiration images per message; the server refuses more. */
export const MAX_REFERENCES = 3;

/** An inspiration image chosen for the next message. */
export interface PendingReference {
  key: string;
  /** A local preview of the chosen file. */
  src: string;
  /**
   * - `looking`: being prepared, uploaded and looked at by the admin.
   * - `ready`: it can be sent (the admin's look is optional help, so a failed look is still ready).
   * - `failed`: it couldn't be uploaded; the owner removes it.
   */
  status: "looking" | "ready" | "failed";
  asset?: AssetRef;
}

/** An inspiration image as it goes out with a message: the upload, and the local preview shown until the server has it. */
export interface SentReference { asset: AssetRef; src: string }

/**
 * The inspiration images waiting above the message box. Each is prepared in the browser (a small
 * JPEG only; it never goes on the site), uploaded privately, then looked at by the admin as a
 * reference so the notes are stored with the message. Sending waits until every one is ready.
 */
export function useReferences({ api, siteId }: { api: BuilderApi; siteId: string }) {
  const [items, setItemsState] = useState<PendingReference[]>([]);
  const itemsRef = useRef<PendingReference[]>([]);
  // Every preview made for this site, revoked together when the session ends (sent ones are still on screen until then).
  const urls = useRef(new Set<string>());
  // Bumped when the hook unmounts or moves to another site; work started under an older epoch is dropped.
  const epoch = useRef(0);

  const setItems = useCallback((next: (current: PendingReference[]) => PendingReference[]) => {
    itemsRef.current = next(itemsRef.current);
    setItemsState(itemsRef.current);
  }, []);

  useEffect(() => () => {
    epoch.current += 1;
    for (const url of urls.current) URL.revokeObjectURL(url);
    urls.current.clear();
    itemsRef.current = [];
    setItemsState([]);
  }, [api, siteId]);

  /** Adds files up to the limit, and returns how many were left out. */
  const add = useCallback((files: File[]): number => {
    const room = Math.max(0, MAX_REFERENCES - itemsRef.current.length);
    const chosen = files.slice(0, room);
    const run = epoch.current;
    for (const file of chosen) {
      const key = `reference-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const src = URL.createObjectURL(file);
      urls.current.add(src);
      setItems((current) => [...current, { key, src, status: "looking" }]);
      const settle = (change: Partial<PendingReference>) => {
        if (run === epoch.current) setItems((current) => current.map((item) => item.key === key ? { ...item, ...change } : item));
      };
      void (async () => {
        try {
          const asset = await api.uploadPhoto(siteId, await prepareReference(file));
          // Waiting for the look keeps its notes with the message; if it fails, the admin still sees the image.
          await api.describePhoto(siteId, asset, "reference").catch(() => null);
          settle({ status: "ready", asset });
        } catch {
          settle({ status: "failed" });
        }
      })();
    }
    return files.length - chosen.length;
  }, [api, siteId, setItems]);

  const remove = useCallback((key: string) => {
    const item = itemsRef.current.find((candidate) => candidate.key === key);
    if (item) {
      URL.revokeObjectURL(item.src);
      urls.current.delete(item.src);
    }
    setItems((current) => current.filter((candidate) => candidate.key !== key));
  }, [setItems]);

  /** Hands the ready images to a message being sent and clears them from the message box; the same image twice goes once. */
  const take = useCallback((): SentReference[] => {
    const sent: SentReference[] = [];
    for (const item of itemsRef.current) {
      if (item.status === "ready" && item.asset && !sent.some((kept) => kept.asset.sha256 === item.asset!.sha256)) sent.push({ asset: item.asset, src: item.src });
    }
    setItems((current) => current.filter((item) => item.status !== "ready"));
    return sent;
  }, [setItems]);

  /** Puts images back above the message box when their message wasn't accepted. */
  const restore = useCallback((sent: SentReference[]) => {
    setItems((current) => [
      ...sent.filter((reference) => !current.some((item) => item.asset?.sha256 === reference.asset.sha256))
        .map((reference, index) => ({ key: `returned-${Date.now().toString(36)}-${index}`, src: reference.src, status: "ready" as const, asset: reference.asset })),
      ...current,
    ].slice(0, MAX_REFERENCES));
  }, [setItems]);

  return {
    items,
    add, remove, take, restore,
    /** Still being prepared or looked at: the message waits. */
    looking: items.some((item) => item.status === "looking"),
    /** One couldn't be uploaded: the owner removes it before sending. */
    failed: items.some((item) => item.status === "failed"),
    ready: items.filter((item) => item.status === "ready").length,
  };
}

export type ReferencesController = ReturnType<typeof useReferences>;
